import { FC, useEffect, useState } from 'react'
import {
  JoinRequestResponseDto,
  OrgMembershipApiClient,
} from '../../features/organization-membership/OrgMembershipSlice'
import useApi from '../../common/utils/api/useApi'
import {
  Flex,
  Text,
  Heading,
  Center,
  VStack,
  Button,
  useToast,
  HStack,
  SimpleGrid,
  Spinner,
} from '@chakra-ui/react'
import { Logo } from '../../components/shared-components/Logo'
import { useNavigate } from 'react-router-dom'
import { routes } from '../routes'
import { MembershipEventStatus } from '../../features/organization-membership/type/MemberShipEventStatus'
import { format } from 'date-fns'

const JoinOrganizationPendingPage: FC = () => {
  const [joinRequest, setJoinRequest] = useState<JoinRequestResponseDto | null>()
  const navigate = useNavigate()
  const toast = useToast()
  const { trigger: getJoinRequests, loading: getJoinRequestsLoading } = useApi(
    OrgMembershipApiClient.getPendingJoinRequests,
    {
      onSuccess: (res) => {
        if (!res.length) {
          setJoinRequest(null)
          navigate(routes.selectRoles.path)
        } else {
          setJoinRequest(res[0])
        }
      },
      onFailure: () => {
        setJoinRequest(null)
        toast({
          title: 'Failed to load your join request. Please try again.',
          status: 'error',
          duration: 5000,
          position: 'top',
          isClosable: true,
        })
      },
    }
  )
  const { trigger: cancelJoinRequest, loading: cancelJoinRequestLoading } = useApi(
    OrgMembershipApiClient.updateJoinRequest,
    {
      onSuccess: () => {
        toast({
          title: 'Successfully canceled join request.',
          status: 'success',
          duration: 2000,
          position: 'top',
          isClosable: true,
          onCloseComplete: () => {
            setJoinRequest(null)
            navigate(routes.selectRoles.path)
          },
        })
      },
      onFailure: () => {
        toast({
          title: 'Failed to cancel the join request, Please try again.',
          status: 'error',
          duration: 5000,
          position: 'top',
          isClosable: true,
        })
      },
    }
  )

  useEffect(() => {
    getJoinRequests({})
  }, [])

  const handleCancel = ({ token }: { token?: string }) => {
    if (token) {
      cancelJoinRequest({ token, status: MembershipEventStatus.CANCELLED })
    } else {
      toast({
        title: 'Failed to cancel join request, Please try again.',
        status: 'error',
        duration: 5000,
        position: 'top',
        isClosable: true,
      })
    }
  }

  if (!joinRequest) {
    if (getJoinRequestsLoading || joinRequest === undefined) {
      return (
        <Center minHeight="100%">
          <Spinner size="xl" thickness="4px" color="blue.500" />
        </Center>
      )
    }
    return null
  }

  return (
    <VStack overflow="auto" p={{ base: '12px', md: '24px' }} minHeight="100%" spacing="16">
      <Center>
        <Logo maxWidth={150} />
      </Center>
      <Flex
        direction="column"
        gap="6"
        shadow="2xl"
        borderRadius="md"
        p="12"
        mt="12"
        width={{ base: '100%', md: '60%' }}
        margin={'auto'}
      >
        <Heading as="h2" alignSelf="center" size="lg" mt={1}>
          Requested Organization Information
        </Heading>
        <VStack
          flexGrow="1"
          overflow="auto"
          flexDirection="column"
          alignItems="center"
          spacing={10}
          width="100%"
        >
          <Text fontSize="sm" color="gray.600">
            You already have a requested organization. The information below shows the
            organization's details and you can click the button below to cancel your request.
          </Text>
          <SimpleGrid columns={{ md: 1, lg: 2 }} spacing={4} alignItems="start" width="100%">
            <HStack justifyContent="space-between" width="100%">
              <Text fontWeight="bold" fontSize={{ base: 'md', lg: 'lg' }}>
                Organization Name:
              </Text>
              <Text flex={1}>{joinRequest?.organizationName}</Text>
            </HStack>
            <HStack justifyContent="space-between" width="100%">
              <Text fontWeight="bold" fontSize={{ base: 'md', lg: 'lg' }}>
                Requested Role:
              </Text>
              <Text flex={1}>{joinRequest?.requestedRole}</Text>
            </HStack>
            <HStack justifyContent="space-between" width="100%">
              <Text fontWeight="bold" fontSize={{ base: 'md', lg: 'lg' }}>
                Requested time:
              </Text>
              <Text flex={1}>{format(new Date(joinRequest?.createdAt), 'dd/MM/yyyy HH:mm')}</Text>
            </HStack>
            <HStack justifyContent="space-between" width="100%">
              <Text fontWeight="bold" fontSize={{ base: 'md', lg: 'lg' }}>
                Expires time:
              </Text>
              <Text flex={1}>{format(new Date(joinRequest?.expiresAt), 'dd/MM/yyyy HH:mm')}</Text>
            </HStack>
          </SimpleGrid>

          <Button
            colorScheme="red"
            onClick={() => handleCancel({ token: joinRequest?.token })}
            isLoading={cancelJoinRequestLoading}
          >
            Cancel Request
          </Button>
        </VStack>
      </Flex>
    </VStack>
  )
}

export default JoinOrganizationPendingPage
